<template>
    <ais-instant-search-ssr class="gap-x-14 lg:flex">
        <ais-configure :hits-per-page.camel="1000" :filters="defaultFilters" />

        <div class="flex-none lg:hidden">
            <div
                class="btn-primary flex items-center justify-center gap-x-2"
                @click="mobileMenuOpen = true"
            >
                Filter <Icon name="bars-filter-solid" class="text-xl" />
            </div>
            <transition name="slide-up">
                <div
                    v-show="mobileMenuOpen"
                    class="fixed inset-x-0 top-0 z-[999999] h-screen overflow-y-scroll bg-white px-4 py-4"
                >
                    <div class="mb-4 flex justify-between">
                        <h3 class="text-xl">Lead Times Filter</h3>
                        <Icon
                            name="xmark-solid"
                            class="text-xl"
                            @click="mobileMenuOpen = false"
                        />
                    </div>
                    <LeadTimesFiltersLists
                        :type="type"
                        :mobile-menu-open="mobileMenuOpen"
                    />
                    <div class="mt-4 flex">
                        <div
                            class="btn-primary w-full text-center"
                            @click="mobileMenuOpen = false"
                        >
                            <ais-stats>
                                <template #default="{ nbHits }">
                                    <div class="text-white">
                                        View {{ nbHits }}
                                        <span v-if="nbHits > 1">results</span
                                        ><span v-else>result</span>
                                    </div>
                                </template>
                            </ais-stats>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
        <aside class="flex-none lg:top-32 lg:w-1/4 lg:self-start">
            <div class="hidden lg:block">
                <LeadTimesFiltersLists :type="type" />
            </div>

            <div
                v-if="$slots['product-filter-cta']"
                class="mt-8 hidden lg:block"
            >
                <slot name="product-filter-cta" />
            </div>
        </aside>
        <div class="mt-8 flex-1 lg:mt-0">
            <div class="w-full lg:min-h-[44px]">
                <ais-current-refinements class="mb-4 flex gap-2">
                    <template #default="{ items, createURL }">
                        <template v-for="item in items" :key="item.attribute">
                            <div
                                v-for="refinement in item.refinements"
                                :key="
                                    [
                                        refinement.attribute,
                                        refinement.type,
                                        refinement.value,
                                        refinement.operator,
                                    ].join(':')
                                "
                                class="rounded-md bg-white shadow-md hover:bg-gray-100"
                            >
                                <a
                                    :href="createURL(refinement)"
                                    class="flex items-center gap-2 px-5 py-3 text-xs uppercase text-grind no-underline"
                                    @click.prevent="item.refine(refinement)"
                                >
                                    {{ refinement.label }}
                                    <Icon
                                        name="filter-x"
                                        class="text-xs text-grind"
                                    />
                                </a>
                            </div>
                        </template>
                    </template>
                </ais-current-refinements>
            </div>

            <ais-infinite-hits>
                <template #default="{ items, isLastPage }">
                    <LeadTimesResults :items="items"></LeadTimesResults>
                    <div v-if="!isLastPage" class="mt-10 text-center">
                        <button class="btn-primary" @click="showMore()">
                            Show More
                        </button>
                    </div>
                </template>
            </ais-infinite-hits>
            <div class="invisible">
                <!-- This exists solely for Google to crawl, hence why it's invisible to the user: -->
                <ais-pagination></ais-pagination>
            </div>

            <div
                v-if="$slots['product-filter-cta-mobile']"
                class="mb-12 mt-8 md:mb-20 lg:hidden"
            >
                <slot name="product-filter-cta-mobile" />
            </div>
        </div>
    </ais-instant-search-ssr>
</template>

<script lang="ts" setup>
import {
    AisConfigure,
    AisCurrentRefinements,
    AisInfiniteHits,
    AisInstantSearchSsr,
    AisPagination,
    AisStats,
    createServerRootMixin,
} from 'vue-instantsearch/vue3/es';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import { ref } from 'vue';
import { useRuntimeConfig } from '#app/nuxt';
import LeadTimesFiltersLists from '~/components/page-building/lead-times/LeadTimesFiltersLists.vue';
import LeadTimesResults from '~/components/page-building/lead-times/LeadTimesResults.vue';

const props = defineProps<{
    type?: string;
    industry?: string;
}>();

const algoliaIndex = computed<string>(() => {
    return useRuntimeConfig().public.appEnv + '_productturnaround';
});

const defaultFilters = computed<string>(() => {
    return '';
});

const mobileMenuOpen = ref(false);
const indexName = algoliaIndex.value;
const route = useRuntimeConfig().public.baseUrl + useRoute().path;
const includePageInRoute = ref(true);
const initialPage = 0;

const decodeIndividualFilterChoices = (choices: any) => {
    for (let i = 0; i < choices.length; i++) {
        choices[i] = decodeURIComponent(choices[i]);
    }
    return choices;
};

const defaultRefinementList: any = {};
const searchSplit = useRequestURL().search.replace('?', '').split('&');

const algolia = useAlgoliaRef();
const serverRootMixin = ref(
    createServerRootMixin({
        searchClient: algolia,
        routing: {
            router: historyRouter({
                getLocation() {
                    if (typeof window === 'undefined') {
                        return new URL(route);
                    }
                    return window.location;
                },
                createURL({ qsModule, routeState, location }) {
                    const baseUrl = useRuntimeConfig().public.baseUrl;
                    const categoryPath = useRoute().path;
                    const queryParameters = {};
                    const page = 0;

                    const queryString = qsModule.stringify(queryParameters, {
                        addQueryPrefix: false,
                        arrayFormat: 'repeat',
                    });

                    let route =
                        baseUrl +
                        categoryPath +
                        (queryString != '' ? '?' + queryString : '');
                    if (!page && queryString === '') {
                        route = baseUrl + categoryPath;
                    }

                    return route;
                },
            }),
        },
        indexName,
        initialUiState: {
            [algoliaIndex.value]: {
                configure: {
                    hitsPerPage: 9,
                    page: initialPage,
                    filters: defaultFilters.value,
                },
                refinementList: defaultRefinementList,
            },
        },
    }),
);

function showMore() {
    includePageInRoute.value = false;
    instantsearch.renderState[indexName].infiniteHits.showMore();
}

const { instantsearch } = serverRootMixin.value.data();
provide('$_ais_ssrInstantSearchInstance', instantsearch);
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
    transition: 0.5s ease;
    top: 0;
}

.slide-up-enter-from,
.slide-up-leave-to {
    top: 100%;
}
</style>
